import {
  useGetSheetDataRows,
  useSheetDataRowUtils,
} from "@/domain/SheetDataRow";
import {
  Sheet,
  useRecursiveDeleteSheet,
  useSetSheet,
  useUpdateSheet,
} from "../../../../domain/Sheet";
import {
  useGetSheetFieldSchemas,
  useSetSheetFieldSchema,
} from "../../../../domain/SheetFieldSchema";
import { ManagedTransaction } from "../../../../firebase/firestore";

import { postDeleteAllRows, postInsertRows } from "@/api/sheets";
import { useDeleteReferredFrom } from "@/domain/ReferredFrom";
import {
  buildTadSheetReferredFromId,
  useDeleteTadSheetReferredFrom,
} from "@/domain/TadSheetReferredFrom";
import { generateId } from "@/utils/generateId";

const duplicateSheetData = async ({
  organizationId,
  source,
  target,
  backend,
  getSheetDataRows,
  sheetDataRowUtils,
}: {
  organizationId: string;
  source: {
    versionId: string;
    sheetId: string;
  };
  target: {
    versionId: string;
    sheetId: string;
  };
  backend?: string;
  getSheetDataRows: ReturnType<typeof useGetSheetDataRows>;
  sheetDataRowUtils: ReturnType<typeof useSheetDataRowUtils>;
}) => {
  const rows = await getSheetDataRows({
    organizationId,
    versionId: source.versionId,
    sheetId: source.sheetId,
  });

  if (rows.length === 0) return;

  if (backend === "lds") {
    await postInsertRows({
      organizationId,
      versionId: target.versionId,
      sheetId: target.sheetId,
      afterRowId: null,
      items: rows.map((r) => ({
        row_id: r.id,
        sheet_data_cells: r.sheetDataCells,
      })),
    });
  } else {
    await sheetDataRowUtils.addRowsByData(
      rows.map((row) => ({
        sheetDataCells: row.sheetDataCells,
      })),
      {
        versionId: target.versionId,
        sheetId: target.sheetId,
      }
    );
  }
};

/**
 * Duplicates a sheet by manually copying schema, field schemas, and data rows.
 *
 * **DEPRECATED for same-version copying**: Use the API endpoint `postCopySheet` instead for
 * copying sheets within the same version. This function should only be used for:
 * - Cross-version sheet copying (sourceVersionId !== versionId)
 * - Special cases where direct Firestore manipulation is required
 *
 * The API endpoint `/sheets/{sheet_id}/copy` is preferred for same-version copying because:
 * - It handles both Firestore and LDS backends correctly
 * - LDS copying is delegated to LDS's native copy operation
 * - It's more efficient and maintainable
 *
 * @see postCopySheet in @/api/sheets for same-version copying
 */
export const duplicateRecursiveSheetData = async ({
  organizationId,
  versionId,
  sourceSheet,
  sourceVersionId,
  setSheet,
  getSheetFieldSchemas,
  setSheetFieldSchema,
  getSheetDataRows,
  sheetDataRowUtils,
  displayName,
  name,
  sort,
}: {
  sourceSheet: Sheet;
  sourceVersionId: string;
  setSheet: ReturnType<typeof useSetSheet>;
  getSheetFieldSchemas: ReturnType<typeof useGetSheetFieldSchemas>;
  setSheetFieldSchema: ReturnType<typeof useSetSheetFieldSchema>;
  getSheetDataRows: ReturnType<typeof useGetSheetDataRows>;
  sheetDataRowUtils: ReturnType<typeof useSheetDataRowUtils>;
  displayName: Sheet["displayName"];
  name: Sheet["name"];
  sort: number;
  organizationId: string;
  versionId: string;
}) => {
  const sheetFieldSchemas = await getSheetFieldSchemas({
    sheetId: sourceSheet.id,
    versionId: sourceVersionId,
  });

  const newSheetId = generateId();

  // 新規シート作成
  await ManagedTransaction.runTransaction(async (transaction) => {
    await setSheet(
      {
        ...sourceSheet,
        firstRowId: null,
        id: newSheetId,
        displayName,
        name,
        sort,
      },
      {},
      { transaction }
    );

    for (const sheetFieldSchema of sheetFieldSchemas) {
      await setSheetFieldSchema(
        {
          ...sheetFieldSchema,
        },
        { sheetId: newSheetId },
        { transaction }
      );
    }
  });
  await duplicateSheetData({
    organizationId,
    source: {
      versionId: sourceVersionId,
      sheetId: sourceSheet.id,
    },
    target: {
      versionId,
      sheetId: newSheetId,
    },
    backend: sourceSheet.backend,
    getSheetDataRows,
    sheetDataRowUtils,
  });
};

export const deleteRecursiveSheetData = async ({
  sheets,
  selectedSheetIds,
  recursiveDeleteSheet,
  deleteReferredFrom,
  deleteTadSheetReferredFrom,
  organizationId,
  versionId,
  updateSheet,
  setProgressCount,
}: {
  sheets: Sheet[];
  selectedSheetIds: string[];
  recursiveDeleteSheet: ReturnType<typeof useRecursiveDeleteSheet>;
  deleteReferredFrom: ReturnType<typeof useDeleteReferredFrom>;
  deleteTadSheetReferredFrom: ReturnType<typeof useDeleteTadSheetReferredFrom>;
  organizationId: string;
  versionId: string;
  updateSheet: ReturnType<typeof useUpdateSheet>;
  setProgressCount: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const restSheets = sheets.filter(({ id }) => !selectedSheetIds.includes(id));
  for (const ldsSheet of sheets.filter(
    (s) => selectedSheetIds.includes(s.id) && s.backend === "lds"
  )) {
    await postDeleteAllRows({
      organizationId,
      versionId,
      sheetId: ldsSheet.id,
    });
  }
  const deleteQueueList = await Promise.all(
    selectedSheetIds.map(
      async (sheetId) => await recursiveDeleteSheet(sheetId, {})
    )
  );
  // 1. referencing sheetがあった場合、referenced sheetのreferred_fromを先に削除
  // 2. 対象のシートを全削除
  // 3. ソート番号を振り直す
  await ManagedTransaction.runTransaction(async (transaction) => {
    await Promise.all(
      sheets
        .filter((s) => selectedSheetIds.includes(s.id) && !!s.referTo)
        .map(
          async (s) =>
            await deleteReferredFrom(
              `${versionId}:${s.id}`,
              {
                versionId: s.referTo!.versionId,
                sheetId: s.referTo!.sheetId,
              },
              { transaction }
            )
        )
    );
    // 残すと参照先 TADS の物理削除が 409 で永久にブロックされる
    await Promise.all(
      sheets
        .filter((s) => selectedSheetIds.includes(s.id) && !!s.tadReferTo)
        .map(
          async (s) =>
            await deleteTadSheetReferredFrom(
              buildTadSheetReferredFromId(versionId, s.id),
              { tadSheetId: s.tadReferTo!.tadSheetId },
              { transaction }
            )
        )
    );
    setProgressCount(0);
    for (const deleteQueue of deleteQueueList) {
      await deleteQueue.exec(transaction);
      setProgressCount((cnt) => cnt + 1);
    }
    // NOTE: 既存のドキュメント更新のため、updateDocでOK
    for (const [index, sheet] of restSheets.entries()) {
      await updateSheet(
        { ...sheet, sort: index },
        { sheetId: sheet.id },
        { transaction }
      );
    }
  });
};
